"use client";

import { ReactNode, forwardRef } from "react";
import { Checker, CheckerIcon, Container, Description, Checkbox, Wrapper } from "./input-checkbox.styles";
import { FieldContainer } from "../field-container/field-container";
import { TailwindColors } from "@components/common/types/general";
import { Label } from "../../label/label";

export interface InputCheckboxProps {
    /**
     * ID of the input checkbox.
     */
    id?: string;

    /**
     * Name of the input checkbox.
     */
    name?: string;

    /**
     * Label of the input checkbox.
     */
    label?: string;

    /**
     * Description of the input checkbox.
     */
    description?: string | ReactNode;

    /**
     * Secondary label of the input checkbox. Only applied if `label` isset.
     */
    secondaryLabel?: string;

    /**
     * Hide the label of the input checkbox.
     */
    hideLabel?: boolean | false;

    /**
     * Will open a tooltip on hover the label. Only applied if `label` isset.
     */
    tooltip?: string;

    /**
     * Whether the checkbox should be vertical centered or not.
     */
    verticalCenter?: boolean;

    /**
     * Color of the input checkbox.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * Whether the checkbox should be displayed as a switch field or not.
     */
    checkSwitch?: boolean | false;

    /**
     * Border radius of the input checkbox.
     */
    rounded?: "md" | "lg" | "full";

    /**
     * Size of the input checkbox.
     */
    size?: "sm" | "lg";

    /**
     * Will disable the input checkbox.
     */
    disabled?: boolean | false;

    /**
     * Will display the input checkbox with shadow.
     */
    shadow?: boolean | false;

    /**
     * Mark the input checkbox as required.
     */
    required?: boolean | false;

    /**
     * Apply tabindex attribute in the dom.
     */
    tabIndex?: number;

    /**
     * A callback function which will be fired on change.
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
     * Value of the input checkbox.
     */
    value?: string | number;

    /**
     * Whether the checkbox is checked or not.
     */
    checked?: boolean | false;

    /**
     * Error message.
     */
    error?: string;

    /**
     * Class name of the input checkbox.
     */
    className?: string;
}

export const InputCheckbox = forwardRef<HTMLInputElement, InputCheckboxProps>(
    (
        {
            onChange,
            value,
            checked,
            id,
            name,
            label,
            description,
            secondaryLabel,
            tooltip,
            required,
            hideLabel,
            verticalCenter,
            error,
            color,
            rounded,
            size,
            disabled,
            shadow,
            checkSwitch,
            tabIndex,
            className,
        },
        ref,
    ) => {
        return (
            <FieldContainer
                id={id}
                label={label}
                secondaryLabel={secondaryLabel}
                hideLabel={hideLabel}
                required={required}
                tooltip={tooltip}
                error={error}
                className={className}
            >
                <Container verticalCenter={verticalCenter} size={size}>
                    <Checkbox size={size} checkSwitch={checkSwitch}>
                        <Wrapper
                            type="checkbox"
                            onChange={onChange}
                            checkSwitch={checkSwitch}
                            value={value}
                            checked={checked}
                            color={color}
                            id={id}
                            name={name}
                            shadow={shadow}
                            tabIndex={tabIndex}
                            error={error}
                            ref={ref}
                        />
                        <Checker
                            size={size}
                            checkSwitch={checkSwitch}
                            color={color}
                            rounded={rounded}
                            disabled={disabled}
                            shadow={shadow}
                            error={error}
                        >
                            <CheckerIcon
                                size={size}
                                checkSwitch={checkSwitch}
                                rounded={rounded}
                                aria-hidden="true"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M10.346 3.301a.929.929 0 0 1 1.37 0 1.076 1.076 0 0 1 0 1.456l-4.64 4.94a.929.929 0 0 1-1.37 0L3.284 7.123a1.076 1.076 0 0 1 0-1.456.929.929 0 0 1 1.37 0L6.39 7.513l3.955-4.212z" />
                            </CheckerIcon>
                        </Checker>
                    </Checkbox>
                    {description && (
                        <Description>
                            <Label as="div" label={description} id={id} required={required && hideLabel ? true : false} />
                        </Description>
                    )}
                </Container>
            </FieldContainer>
        );
    },
);

InputCheckbox.displayName = "InputCheckbox";
